/**
 * GLAMRDiP post-bundle upsell modal — Joel 2026-04-30.
 *
 * Lifecycle (single state machine, see `state` below):
 *   idle → preloaded → showing → (accepted | declined | closed) → idle
 *
 * Hook surface:
 *   `window.gdUpsell.maybeShow(bundleData)` returns a Promise<{ accepted: boolean }>
 *   that the kit-builder awaits before opening the cart drawer. If we
 *   short-circuit (disabled, capped, OOS, B2B suppression), the promise
 *   resolves immediately with { accepted: false } and the kit-builder
 *   continues its existing flow.
 *
 * Critical UX rule: the upsell mounts INSIDE the bundle modal panel
 * (.gd-kb-modal__panel). No second overlay, no resize jump. We capture
 * the panel's height/width before swapping content so the cross-fade
 * runs at fixed dimensions; on close we release them.
 *
 * Discount: this module displays the merchant-configured discounted
 * price for visual purposes only. The actual discount is applied by
 * a Shopify Admin "Buy X get Y at N% off" automatic discount rule.
 * When the upsell variant is added to the cart, Shopify auto-detects
 * the kit + upsell pair and applies the discount at the line-item
 * level. We do NOT apply discounts client-side — that wouldn't survive
 * checkout.
 */
(function () {
  'use strict';

  const SESSION_KEY_DECLINED = 'gd:upsell:declined';
  const SESSION_KEY_ACCEPTED = 'gd:upsell:accepted';
  const FADE_MS = 250;

  /** Page-load: parse the inlined config block, bail if absent/disabled. */
  const configEl = document.querySelector('[data-gd-upsell-config]');
  const templateEl = document.querySelector('[data-gd-upsell-template]');
  if (!configEl || !templateEl) return;

  let config;
  try { config = JSON.parse(configEl.textContent); }
  catch (e) { console.warn('[gd-upsell] config parse failed:', e); return; }

  if (!config.enable || !Array.isArray(config.products) || config.products.length === 0) return;

  /** Filter out unconfigured / out-of-stock products on the way in so the
      JS only ever decides between "show available products" or "skip". */
  const candidates = config.products.filter((p) => p && p.id && p.available);
  if (candidates.length === 0) return;

  /** B2B / wholesale suppression. Shopify exposes `customer.b2b?` only via
      Liquid — we can't read it client-side directly. Inferred from the
      presence of `BUYER_B2B` in `window.Shopify.customer` (set by the
      checkout extension when present), or from a body class hook the
      theme can opt into. Either signal disables the upsell. */
  if (config.suppress_b2b) {
    const isB2B = !!(window.Shopify && window.Shopify.customer && window.Shopify.customer.b2b)
      || document.body.classList.contains('is-b2b');
    if (isB2B) return;
  }

  /** Currency formatter — pulls the storefront's locale + currency once.
      Shopify exposes the active currency code on `Shopify.currency.active`;
      fall back to AUD which is the home market. */
  const currencyCode = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
  const moneyLocale = (document.documentElement.lang || 'en-AU').replace('_', '-');
  const fmt = new Intl.NumberFormat(moneyLocale, { style: 'currency', currency: currencyCode });
  const money = (cents) => fmt.format((cents || 0) / 100);

  /** Analytics emit — fire to whichever surface is wired (GA4 dataLayer,
      Klaviyo `_learnq`, or generic `gd:event`). All three are no-ops if
      absent so this can ship before any single tracker is wired. */
  function emit(name, payload) {
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: name }, payload || {}));
      }
      if (window._learnq && typeof window._learnq.push === 'function') {
        window._learnq.push(['track', name, payload || {}]);
      }
      document.dispatchEvent(new CustomEvent('gd:event', { detail: { name, payload } }));
    } catch (e) { /* analytics errors must not block UX */ }
  }

  /** Frequency cap — sessionStorage so the cap clears when the tab closes
      (per spec). If sessionStorage is unavailable (Safari private mode
      historically) we degrade open: better to show the upsell once too
      often than silently break the feature. */
  function isCapped() {
    try {
      return sessionStorage.getItem(SESSION_KEY_DECLINED) === '1';
    } catch (e) { return false; }
  }
  function markDeclined() {
    try { sessionStorage.setItem(SESSION_KEY_DECLINED, '1'); } catch (e) {}
  }
  function markAccepted() {
    try { sessionStorage.setItem(SESSION_KEY_ACCEPTED, '1'); } catch (e) {}
  }

  /** Resolve the cart line items once on demand. Used to skip the modal
      when the upsell product is already in cart (don't sell what they
      already bought). Throws → return empty (degrade open). */
  async function getCartItems() {
    try {
      const r = await fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } });
      if (!r.ok) return [];
      const data = await r.json();
      return Array.isArray(data.items) ? data.items : [];
    } catch (e) { return []; }
  }

  /** Choose which upsell to show. Filters candidates against the live cart
      (skip already-in-cart) and returns the first survivor. We don't yet
      support showing two upsells side-by-side in the same modal — that's
      a v2 once we measure single-upsell accept rate. The schema's max=2
      governs the merchant's *pool*, not what we show simultaneously. */
  async function pickUpsell() {
    const cartItems = await getCartItems();
    const inCartIds = new Set(cartItems.map((it) => it.product_id));
    return candidates.find((p) => !inCartIds.has(p.id)) || null;
  }

  /** Render the chosen product into the cloned template. Returns the
      hydrated element (still detached from DOM — caller mounts it). */
  function renderTemplate(product) {
    const node = templateEl.content.firstElementChild.cloneNode(true);
    // Joel 2026-04-30 (lightbox scope fix): the upsell slides are also
    // [data-lightbox-trigger] elements (tap-to-fullscreen), but they
    // mount inside the bundle modal panel — not inside any of the
    // PDP gallery sections. Without a scope marker the page-level
    // lightbox would mix upsell slides into PDP gallery results.
    // Mark the upsell node as its own scope so its triggers stay
    // isolated.
    node.setAttribute('data-lightbox-scope', 'upsell-' + product.id);

    // Gallery — video first if configured, then product images. Track
    // is horizontally scrollable; we add scroll-snap + drag handlers
    // below. Each slide is 100% wide.
    const track = node.querySelector('[data-gd-upsell-track]');
    const dotsEl = node.querySelector('[data-gd-upsell-dots]');
    const slides = [];
    // Video object — now per-breakpoint. Liquid emits:
    //   { desktop: { uploaded, sources, poster, alt },
    //     mobile:  { uploaded, sources, poster, alt } }
    //
    // Each breakpoint resolves through a fallback chain (explicit → legacy
    // single field → cross-breakpoint), so merchants who only upload one
    // asset still get something rendered on the other breakpoint.
    //
    // `uploaded:false` means no asset resolves for this breakpoint. We
    // skip the video slide and render product images only.
    // `uploaded:true, sources:[]` means encoding is still in progress —
    // show the poster as a static slide.
    // `uploaded:true, sources:[…]` means playable.
    //
    // prefers-reduced-motion: never autoplay. Prefer poster; fall back
    // to a non-autoplaying <video> with controls.
    const desktopMQ = window.matchMedia('(min-width: 990px)');
    const videoBP = product.video || {};
    function activeBreakpoint() { return desktopMQ.matches ? (videoBP.desktop || {}) : (videoBP.mobile || {}); }
    const v = activeBreakpoint();
    const isUploaded = v.uploaded === true;
    const isPlayable = isUploaded && Array.isArray(v.sources) && v.sources.length > 0;
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function appendSources(videoEl, sources) {
      sources.forEach((src) => {
        if (!src || !src.src) return;
        const s = document.createElement('source');
        s.src = src.src;
        if (src.type) s.type = src.type;
        videoEl.appendChild(s);
      });
    }
    function appendPosterImage(wrap, breakpointObj) {
      const img = document.createElement('img');
      img.src = breakpointObj.poster;
      img.alt = breakpointObj.alt || product.heading || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      wrap.appendChild(img);
    }

    if (isPlayable) {
      const wrap = document.createElement('div');
      wrap.className = 'gd-upsell__slide gd-upsell__slide--video';
      // Stash the per-breakpoint payload on the wrap so the resize
      // handler below can swap source/poster on viewport-cross.
      wrap.__gdVideoBP = videoBP;
      if (reduced) {
        // Suppress autoplay. Prefer poster; fall back to a non-autoplaying
        // <video> with controls so user can opt in.
        if (v.poster) {
          appendPosterImage(wrap, v);
        } else {
          const video = document.createElement('video');
          // ALL four HTML attributes set, even though we don't autoplay.
          // muted+playsinline are required so iOS doesn't take the video
          // fullscreen if the user later taps play. controls allows opt-in.
          video.setAttribute('muted', '');
          video.setAttribute('playsinline', '');
          video.setAttribute('controls', '');
          video.muted = true; video.playsInline = true; video.controls = true;
          video.preload = 'metadata';
          appendSources(video, v.sources);
          wrap.appendChild(video);
          wrap.__gdVideoEl = video;
        }
      } else {
        const video = document.createElement('video');
        // iOS Safari + Chrome silently reject autoplay if ANY of these
        // four HTML attributes are missing. Setting JS properties alone
        // is insufficient on first paint — the parser checks the markup.
        video.setAttribute('autoplay', '');
        video.setAttribute('loop', '');
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        // Mirror as JS properties for browsers that consult both.
        video.muted = true; video.loop = true; video.autoplay = true; video.playsInline = true;
        video.preload = 'metadata';
        if (v.poster) video.poster = v.poster;
        appendSources(video, v.sources);
        // Explicit play() after mount + paint. Some browsers ignore
        // declarative autoplay on elements that were display:none or
        // opacity:0 at insertion time (the upsell starts hidden). We
        // schedule the play() to fire once the panel becomes visible
        // (.gd-upsell--visible class). play() can return a Promise that
        // rejects when autoplay policy denies — fall back to poster.
        wrap.dataset.gdUpsellPendingPlay = '1';
        wrap.__gdVideoEl = video;
        wrap.appendChild(video);
      }
      track.appendChild(wrap);
      slides.push(wrap);

      // Viewport-cross handler: when the user drags a desktop window
      // narrower than 990 (or vice versa) WITH the modal open, swap
      // the video's <source> children to the matching breakpoint and
      // re-call play() if the new asset is playable. Edge case but
      // cheap to handle.
      const onMQ = (e) => {
        const next = e.matches ? (videoBP.desktop || {}) : (videoBP.mobile || {});
        const playable = next.uploaded && Array.isArray(next.sources) && next.sources.length > 0;
        const videoEl = wrap.__gdVideoEl;
        if (!videoEl || !playable) return;
        // Replace sources, preserve poster + autoplay state.
        videoEl.innerHTML = '';
        appendSources(videoEl, next.sources);
        if (next.poster) videoEl.poster = next.poster;
        videoEl.load();
        if (!reduced) videoEl.play().catch(() => {});
      };
      // Both old (addListener) and new (addEventListener) APIs — Safari
      // 13.x still uses addListener.
      if (desktopMQ.addEventListener) desktopMQ.addEventListener('change', onMQ);
      else if (desktopMQ.addListener) desktopMQ.addListener(onMQ);
    } else if (isUploaded && v.poster) {
      // Encoding-in-progress state. Render poster only — when sources
      // populate on a future page load, the video will play normally.
      const wrap = document.createElement('div');
      wrap.className = 'gd-upsell__slide gd-upsell__slide--video gd-upsell__slide--encoding';
      appendPosterImage(wrap, v);
      track.appendChild(wrap);
      slides.push(wrap);
    }
    // else: no video uploaded for this breakpoint (and no fallback
    // chain match) → skip; product images take over the gallery.
    (product.images || []).forEach((img, i) => {
      const wrap = document.createElement('div');
      wrap.className = 'gd-upsell__slide';
      // Tap-to-fullscreen path is programmatic (see openSlideLightbox
      // below) — it builds an explicit images array from upsell-only
      // slides and calls lb.open({ images, startIndex }). No
      // data-lightbox-trigger attribute, so the shared lightbox shim
      // never sees these slides; structurally impossible to leak
      // upsell images into the page-level lightbox auto-discovery.
      wrap.dataset.imageFull = img.src;        // 1200w variant
      wrap.dataset.imageAlt  = img.alt || product.heading || '';
      wrap.setAttribute('role', 'button');
      wrap.setAttribute('aria-label', 'Tap to view larger');
      wrap.tabIndex = 0;
      wrap.style.cursor = 'zoom-in';
      const tag = document.createElement('img');
      tag.src = img.src;
      tag.srcset = `${img.src600} 600w, ${img.src900} 900w, ${img.src} 1200w`;
      tag.sizes = '(max-width: 989px) 100vw, 50vw';
      tag.alt = img.alt || product.heading || '';
      tag.loading = i === 0 ? 'eager' : 'lazy';
      tag.decoding = 'async';
      tag.width = img.width || 1200;
      tag.height = img.height || 1500;
      wrap.appendChild(tag);
      track.appendChild(wrap);
      slides.push(wrap);
    });

    // Pagination dots (one per slide). Click → scroll to slide.
    slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'gd-upsell__dot' + (i === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () => {
        track.scrollTo({ left: slides[i].offsetLeft, behavior: 'smooth' });
      });
      dotsEl.appendChild(dot);
    });

    // Active-dot tracking — scroll listener with rAF coalescing so we
    // don't churn during drag/swipe.
    let raf = 0;
    track.addEventListener('scroll', () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const x = track.scrollLeft + track.clientWidth / 2;
        let active = 0;
        for (let i = 0; i < slides.length; i++) {
          if (slides[i].offsetLeft <= x) active = i;
        }
        const dotEls = dotsEl.querySelectorAll('.gd-upsell__dot');
        dotEls.forEach((d, i) => d.classList.toggle('is-active', i === active));
      });
    }, { passive: true });

    // Prev/next buttons. Hidden if only 1 slide.
    const prev = node.querySelector('[data-gd-upsell-prev]');
    const next = node.querySelector('[data-gd-upsell-next]');
    if (slides.length <= 1) {
      prev.hidden = true; next.hidden = true; dotsEl.hidden = true;
    } else {
      prev.addEventListener('click', () => {
        const w = track.clientWidth;
        track.scrollBy({ left: -w, behavior: 'smooth' });
      });
      next.addEventListener('click', () => {
        const w = track.clientWidth;
        track.scrollBy({ left:  w, behavior: 'smooth' });
      });
    }

    // Tap-to-fullscreen: pointer-move threshold disambiguates a tap from
    // a horizontal swipe. <10px movement on pointerup → open lightbox at
    // the slide's index. ≥10px movement → user was swiping, suppress.
    // Pagination dot clicks use stopPropagation so they don't fire here.
    // Skip slides marked --video; the page-level lightbox renders <img>
    // and doesn't yet support video playback (follow-up if needed).
    //
    // STRUCTURAL FIX (Joel 2026-04-30): the lightbox API now requires
    // an EXPLICIT images array — no auto-discovery. We pass only the
    // upsell product's own images, with the tapped slide as startIndex.
    // No DOM-trigger lookup, no global-array slicing, no scope leak
    // possible. The lightbox is structurally incapable of mixing
    // surfaces because callers always tell it exactly what to show.
    function openSlideLightbox(localIdx) {
      try {
        const lb = window.__gdLightbox;
        if (!lb) return;
        // Only image slides go to the lightbox; videos render inline.
        const imageSlides = slides.filter((s) =>
          !s.classList.contains('gd-upsell__slide--video') &&
          !s.classList.contains('gd-upsell__slide--encoding')
        );
        const images = imageSlides.map((s) => ({
          full: s.dataset.imageFull || s.querySelector('img')?.src || '',
          alt:  s.dataset.imageAlt || product.heading || '',
        }));
        // localIdx is into `slides` (which includes videos at index 0
        // when present). Map to the imageSlides position so startIndex
        // matches the actual array we're passing.
        const startIndex = imageSlides.indexOf(slides[localIdx]);
        if (startIndex < 0 || images.length === 0) return;
        lb.open({ images, startIndex, returnFocus: slides[localIdx] });
      } catch (e) { console.warn('[gd-upsell] lightbox open failed:', e); }
    }
    slides.forEach((slide, i) => {
      if (slide.classList.contains('gd-upsell__slide--video') ||
          slide.classList.contains('gd-upsell__slide--encoding')) return;
      let downX = 0, downY = 0, moved = false;
      slide.addEventListener('pointerdown', (e) => {
        downX = e.clientX; downY = e.clientY; moved = false;
      });
      slide.addEventListener('pointermove', (e) => {
        if (!moved) {
          const dx = Math.abs(e.clientX - downX);
          const dy = Math.abs(e.clientY - downY);
          if (dx > 10 || dy > 10) moved = true;
        }
      });
      slide.addEventListener('pointerup', (e) => {
        // Don't fire when the pointer ended inside an interactive child
        // (pagination dots stopPropagation, but defensive belt).
        if (moved) return;
        if (e.target && e.target.closest('.gd-upsell__dot, .gd-upsell__gallery-nav, [data-gd-upsell-close], [data-gd-upsell-accept], [data-gd-upsell-decline]')) return;
        e.preventDefault();
        openSlideLightbox(i);
      });
      // Keyboard activation for screen-reader / keyboard users.
      slide.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openSlideLightbox(i);
        }
      });
    });
    // Stop pagination dot click from bubbling up to the slide tap handler.
    [...dotsEl.querySelectorAll('button')].forEach((b) => {
      b.addEventListener('pointerup', (e) => e.stopPropagation());
    });

    // Badge — top-left of media. Hidden if disabled or no text.
    const badgeEl = node.querySelector('[data-gd-upsell-badge]');
    if (product.badge && product.badge.enable && product.badge.text) {
      badgeEl.textContent = product.badge.text;
      badgeEl.hidden = false;
    }

    // Right column copy.
    const eyebrowEl  = node.querySelector('[data-gd-upsell-eyebrow]');
    const headingEl  = node.querySelector('[data-gd-upsell-heading]');
    const priceOrigEl= node.querySelector('[data-gd-upsell-price-original]');
    const priceCurEl = node.querySelector('[data-gd-upsell-price-current]');
    const pricePillEl= node.querySelector('[data-gd-upsell-price-pill]');
    const includedEl = node.querySelector('[data-gd-upsell-included]');
    const socialEl   = node.querySelector('[data-gd-upsell-social]');
    const acceptBtn  = node.querySelector('[data-gd-upsell-accept]');
    const declineBtn = node.querySelector('[data-gd-upsell-decline]');

    eyebrowEl.textContent = product.eyebrow || '';
    if (!product.eyebrow) eyebrowEl.hidden = true;
    headingEl.textContent = product.heading || product.title;

    // Joel 2026-05-01: pricing derives from product data only — full
    // = variant.compare_at_price (or variant.price if no compare-at);
    // display = variant.price. Section settings no longer specify a
    // discount type/value. The % pill is computed from the two values
    // directly so any compare-at config in Shopify Admin reflects in
    // the modal automatically — and the modal price always matches
    // checkout (which applies cart-level discount rules at the cart
    // level natively). See CLAUDE.md "Upsell modal pricing" rule.
    const isDiscounted = product.display_price_cents < product.full_price_cents;
    if (isDiscounted) {
      priceOrigEl.hidden = false;
      priceOrigEl.textContent = money(product.full_price_cents);
      priceCurEl.textContent  = money(product.display_price_cents);
      // Joel 2026-05-02: pill repurposed from "−X%" → "Save $Y". Brand
      // pricing display convention site-wide uses absolute savings, not
      // percentage. Same .gd-upsell__price-pill element + data attr;
      // CSS below scales it down to a 12px brand-violet caption right-
      // aligned under the sale price.
      const saveCents = product.full_price_cents - product.display_price_cents;
      if (saveCents > 0) {
        // Joel 2026-05-02 (revision): pill text "SAVE $X" — CSS uppercase
        // is already applied via text-transform on the pill rule, but
        // keeping the literal capital `SAVE` matches the brand's other
        // pill conventions for non-CSS readers (screen readers etc).
        pricePillEl.textContent = `${(config.save_prefix || 'Save').toUpperCase()} ${money(saveCents)}`;
        pricePillEl.hidden = false;
      } else {
        pricePillEl.hidden = true;
      }
    } else {
      // No compare-at price set OR compare-at <= price → just show
      // the regular price. No strikethrough, no pill.
      priceOrigEl.hidden = true;
      priceCurEl.textContent = money(product.full_price_cents);
      pricePillEl.hidden = true;
    }

    if (product.included_html) {
      // included_html comes from a Shopify richtext setting — already
      // sanitized by the editor (no script tags). innerHTML is safe here.
      const whatsIncluded = (config.whats_included_label && String(config.whats_included_label).trim()) || "What's included?";
      includedEl.innerHTML = `<h3 class="gd-upsell__included-h">${escapeHtml(whatsIncluded)}</h3>${product.included_html}`;
      // On mobile, cap visible bullets to 3 with a "+N more" toggle so
      // long lists don't blow out the vertical budget below the sticky
      // CTA. Desktop always shows the full list (CSS undoes the clamp).
      const ul = includedEl.querySelector('ul, ol');
      if (ul) {
        const items = ul.querySelectorAll('li');
        if (items.length > 3) {
          const hidden = items.length - 3;
          includedEl.classList.add('is-clamped');
          const toggle = document.createElement('button');
          toggle.type = 'button';
          toggle.className = 'gd-upsell__included-more';
          toggle.setAttribute('aria-expanded', 'false');
          const setLabel = (expanded) => {
            toggle.textContent = expanded ? 'Show less' : `+${hidden} more`;
            toggle.setAttribute('aria-expanded', String(expanded));
          };
          setLabel(false);
          toggle.addEventListener('click', () => {
            const expanded = includedEl.classList.toggle('is-expanded');
            setLabel(expanded);
          });
          includedEl.appendChild(toggle);
        }
      }
    } else {
      includedEl.hidden = true;
    }

    if (product.social_proof) {
      socialEl.innerHTML = `<span class="gd-upsell__social-icon" aria-hidden="true"></span>${escapeHtml(product.social_proof)}`;
    } else {
      socialEl.hidden = true;
    }

    const acceptLabel = acceptBtn.querySelector('[data-gd-upsell-accept-label]') || acceptBtn;
    acceptLabel.textContent = product.primary_cta || 'Add to cart now';
    declineBtn.textContent = product.secondary_cta || 'Decline offer · View cart';

    // Wire button handlers — accept/decline are dispatched as events on
    // the modal itself; the orchestrator below owns state transitions.
    acceptBtn.addEventListener('click', () => {
      node.dispatchEvent(new CustomEvent('gd:upsell:accept', { detail: { product }, bubbles: true }));
    });
    declineBtn.addEventListener('click', () => {
      node.dispatchEvent(new CustomEvent('gd:upsell:decline', { detail: { product }, bubbles: true }));
    });
    node.querySelector('[data-gd-upsell-close]').addEventListener('click', () => {
      node.dispatchEvent(new CustomEvent('gd:upsell:close', { detail: { product }, bubbles: true }));
    });

    return node;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
  }

  /** Add the upsell variant to the cart. The Shopify auto-discount rule
      handles the price reduction at the line item level. We send the
      vanilla variant ID; the cart drawer (which we open right after)
      will display the discount Shopify computes. */
  async function addUpsellToCart(product) {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: product.first_variant_id, quantity: 1, properties: { _upsell: 'true' } }] }),
    });
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body.description || 'Cart add failed');
    }
    return r.json();
  }

  /** Mount the upsell DOM into the bundle modal panel and run the
      cross-fade. Returns a promise resolving { accepted: boolean }
      after user interaction (or modal close). */
  function showUpsell(product, bundleData) {
    return new Promise((resolve) => {
      const panel = document.querySelector('[data-gd-kit-builder]');
      const modal = document.querySelector('[data-gd-kit-builder-modal]');
      if (!panel || !modal) { resolve({ accepted: false }); return; }

      // Capture the panel's current size so the cross-fade runs at
      // fixed dimensions (no resize jump while content swaps).
      const rect = panel.getBoundingClientRect();
      const isMobileLayout = window.matchMedia('(max-width: 989px)').matches;
      // Mobile is full-screen already; only lock dimensions on desktop.
      if (!isMobileLayout) {
        panel.style.minHeight = rect.height + 'px';
      }

      const upsellNode = renderTemplate(product);
      // Mount as an absolute-positioned overlay INSIDE the panel so it
      // shares the panel's bounds. The bundle content sits underneath
      // (still in DOM, just opacity:0) so we can cross-fade back if
      // we ever want to. For simplicity v1 doesn't fade back — accept
      // / decline both terminate to cart-drawer-open.
      upsellNode.classList.add('gd-upsell--mounting');
      panel.appendChild(upsellNode);
      // Hide the bundle children (everything except the upsell we
      // just appended) so the upsell is the only visible content.
      const bundleChildren = [...panel.children].filter((c) => c !== upsellNode);
      bundleChildren.forEach((c) => { c.dataset.gdUpsellHidden = '1'; c.style.opacity = '0'; c.style.pointerEvents = 'none'; });

      // Force-paint then unhide the upsell to trigger CSS opacity transition.
      upsellNode.removeAttribute('hidden');
      requestAnimationFrame(() => {
        upsellNode.classList.remove('gd-upsell--mounting');
        upsellNode.classList.add('gd-upsell--visible');

        // Now the panel is visible (opacity will transition to 1 over
        // 250ms). Kick off any pending video plays. Browsers reject
        // play() with a NotAllowedError if autoplay policy denies — we
        // catch and fall back to showing the poster (already set on
        // the <video> element via the `poster` attribute, so the user
        // sees the poster frame and the video sits idle until they
        // tap to play). Logged so the merchant can debug encoding /
        // policy issues without silent failure.
        upsellNode.querySelectorAll('[data-gd-upsell-pending-play]').forEach((slot) => {
          const videoEl = slot.__gdVideoEl;
          if (!videoEl) return;
          slot.removeAttribute('data-gd-upsell-pending-play');
          // Slight defer so the panel's opacity transition has begun
          // before play() — avoids browsers that gate play on visibility.
          setTimeout(() => {
            const p = videoEl.play();
            if (p && typeof p.catch === 'function') {
              p.catch((err) => {
                console.warn('[gd-upsell] video autoplay denied; showing poster:', err && err.name);
                // Reveal a play button so the user can opt in.
                videoEl.controls = true;
                videoEl.setAttribute('controls', '');
              });
            }
          }, 60);
        });
      });

      // Move focus to primary CTA per spec (configurable; primary is
      // the spec default). Wait one frame so the element is visible.
      requestAnimationFrame(() => {
        const btn = upsellNode.querySelector('[data-gd-upsell-accept]');
        if (btn) btn.focus({ preventScroll: true });
      });

      // Focus trap — only Tab/Shift+Tab, ESC closes.
      function onKey(e) {
        if (e.key === 'Escape') {
          e.preventDefault();
          finalize('closed');
        } else if (e.key === 'Tab') {
          const focusables = upsellNode.querySelectorAll('button, [href], input, select, [tabindex]:not([tabindex="-1"])');
          if (!focusables.length) return;
          const first = focusables[0];
          const last  = focusables[focusables.length - 1];
          if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
          else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
        }
      }
      document.addEventListener('keydown', onKey);

      // Outcome handlers. `outcome` is one of 'accepted' | 'declined' | 'closed'.
      let settled = false;
      const errorEl = upsellNode.querySelector('[data-gd-upsell-error]');
      const acceptBtn = upsellNode.querySelector('[data-gd-upsell-accept]');

      async function finalize(outcome) {
        if (settled) return;
        settled = true;
        document.removeEventListener('keydown', onKey);

        if (outcome === 'accepted') {
          markAccepted();
          emit('upsell_modal_accepted', { upsell_id: product.id, upsell_handle: product.handle, bundle_id: (bundleData && bundleData.bundle_id) || null });
        } else if (outcome === 'declined') {
          markDeclined();
          emit('upsell_modal_declined', { upsell_id: product.id, upsell_handle: product.handle });
        } else {
          markDeclined();
          emit('upsell_modal_closed_via_x', { upsell_id: product.id, upsell_handle: product.handle });
        }

        // Cross-fade upsell out, then unmount. Bundle children are
        // removed from DOM along with the upsell when the bundle modal
        // closes — kit-builder owns that. We just clean up our mount.
        upsellNode.classList.remove('gd-upsell--visible');
        upsellNode.classList.add('gd-upsell--leaving');
        setTimeout(() => {
          if (upsellNode.parentNode) upsellNode.parentNode.removeChild(upsellNode);
          // Restore bundle children visibility — kit-builder may want to
          // reuse the modal if the user reopens it.
          bundleChildren.forEach((c) => {
            delete c.dataset.gdUpsellHidden;
            c.style.opacity = '';
            c.style.pointerEvents = '';
          });
          if (!isMobileLayout) panel.style.minHeight = '';
          resolve({ accepted: outcome === 'accepted' });
        }, FADE_MS);
      }

      // Wire up custom events from the rendered template.
      upsellNode.addEventListener('gd:upsell:accept', async () => {
        if (acceptBtn.classList.contains('is-loading')) return;
        acceptBtn.classList.add('is-loading');
        acceptBtn.setAttribute('aria-busy', 'true');
        acceptBtn.disabled = true;
        try {
          await addUpsellToCart(product);
          // Klaviyo "Added to Cart" — upsell pathway. Standard event in
          // addition to the existing custom upsell_modal_accepted dispatch
          // so cart-abandonment flows match this surface like any ATC.
          try {
            if (window._learnq && typeof window._learnq.push === 'function') {
              const priceDollars = (product.price_cents != null)
                ? product.price_cents / 100
                : (product.display_price_cents != null ? product.display_price_cents / 100 : 0);
              window._learnq.push(['track', 'Added to Cart', {
                $value: priceDollars,
                AddedItemProductName: product.title || product.heading || '',
                AddedItemProductID:   product.id ? String(product.id) : '',
                AddedItemQuantity:    1,
                AddedItemPrice:       priceDollars,
                AddedItemURL:         product.handle ? (window.location.origin + '/products/' + product.handle) : '',
                AddedItemImageURL:    (product.images && product.images[0]) || product.image || (product.featured_image && product.featured_image.url) || '',
                Source:               'upsell_modal',
              }]);
            }
          } catch (e) {}
          acceptBtn.classList.remove('is-loading');
          acceptBtn.classList.add('is-success');
          // Dispatch a generic cart-added event so cart drawer / cart
          // count refresh wherever it lives can stay in sync.
          document.body.dispatchEvent(new CustomEvent('product-added-to-cart'));
          setTimeout(() => finalize('accepted'), 350);
        } catch (e) {
          acceptBtn.classList.remove('is-loading');
          acceptBtn.removeAttribute('aria-busy');
          acceptBtn.disabled = false;
          if (errorEl) {
            errorEl.textContent = e.message || 'Could not add to cart. Please try again.';
            errorEl.hidden = false;
          }
        }
      });
      upsellNode.addEventListener('gd:upsell:decline', () => finalize('declined'));
      upsellNode.addEventListener('gd:upsell:close',   () => finalize('closed'));

      emit('upsell_modal_shown', { upsell_id: product.id, upsell_handle: product.handle, bundle_id: (bundleData && bundleData.bundle_id) || null });
    });
  }

  /** Public API consumed by kit-builder.js. Awaitable, never throws —
      callers can chain it without try/catch. The contract: this function
      decides whether to show the upsell, and if it does, owns the modal
      until the user resolves it.

      Observability: every guard logs why it's blocking so debugging
      "modal not showing" doesn't require code reading. Joel 2026-04-30
      after second regression report — this prevents future drift from
      silently breaking the trigger. */
  async function maybeShow(bundleData) {
    if (isCapped()) {
      console.log('[upsell] not shown — sessionStorage frequency cap is set (gd:upsell:declined). Run sessionStorage.clear() and reload to test.');
      emit('upsell_modal_skipped', { reason: 'frequency_cap' });
      return { accepted: false };
    }
    const product = await pickUpsell();
    if (!product) {
      console.log('[upsell] not shown — no eligible upsell product. Either all candidates are already in cart, or product is unavailable. Candidates:', candidates.map(c => ({ handle: c.handle, available: c.available })));
      emit('upsell_modal_skipped', { reason: 'no_product' });
      return { accepted: false };
    }
    console.log('[upsell] showing modal', { product: product.handle, bundleData });
    return showUpsell(product, bundleData);
  }

  // Observability: expose a debug helper for dev console.
  function debugGuards() {
    return {
      isCapped: isCapped(),
      sessionStorageDeclined: (() => { try { return sessionStorage.getItem(SESSION_KEY_DECLINED); } catch (e) { return 'unavailable'; } })(),
      sessionStorageAccepted: (() => { try { return sessionStorage.getItem(SESSION_KEY_ACCEPTED); } catch (e) { return 'unavailable'; } })(),
      candidates: candidates.map(c => ({ handle: c.handle, available: c.available, id: c.id })),
      configEnable: config.enable,
      suppressB2B: config.suppress_b2b,
    };
  }

  // Pre-warm: kick off image preload for the first candidate when the
  // bundle builder modal opens. The user should never see a blank gallery
  // even on first load.
  document.addEventListener('gd-kit-modal:opened', () => {
    if (isCapped()) return;
    const first = candidates[0];
    if (!first) return;
    (first.images || []).slice(0, 2).forEach((img) => {
      const link = document.createElement('link');
      link.rel = 'preload'; link.as = 'image'; link.href = img.src;
      link.imageSrcset = `${img.src600} 600w, ${img.src900} 900w, ${img.src} 1200w`;
      link.imageSizes  = '(max-width: 989px) 100vw, 50vw';
      document.head.appendChild(link);
    });
  });

  window.gdUpsell = { maybeShow, isCapped, debugGuards };
})();
